import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useMemo } from 'react';
import { UserRole, RoleHierarchy } from '../types/roles';
import type { UserWithRoles } from '../types/roles';
import { useAbpPermissions } from '../contexts/AbpPermissionsContext';

/**
 * Extract roles from OIDC user profile
 * Roles can be in different claim formats:
 * - role (single or array)
 * - roles (array)
 */
function extractRolesFromUser(user: unknown): UserRole[] {
  if (!user || typeof user !== 'object') {
    return [];
  }

  const userObj = user as Record<string, unknown>;
  const roles: string[] = [];

  // Check 'role' claim (can be string or array)
  if (userObj.role) {
    if (Array.isArray(userObj.role)) {
      roles.push(...userObj.role);
    } else if (typeof userObj.role === 'string') {
      roles.push(userObj.role);
    }
  }

  // Check 'roles' claim (usually array)
  if (userObj.roles && Array.isArray(userObj.roles)) {
    roles.push(...userObj.roles);
  }

  // Filter to only valid UserRole values
  return roles
    .filter((role): role is string => typeof role === 'string')
    .filter((role): role is UserRole =>
      Object.values(UserRole).includes(role as UserRole)
    );
}

/**
 * Enhanced authentication hook with ABP permission-based access control
 *
 * This hook combines OIDC authentication with ABP's permission system.
 * Permissions are fetched from /api/abp/application-configuration and
 * represent the actual granted permissions from the database (not hardcoded).
 */
export function useAuth() {
  const oidcAuth = useOidcAuth();
  const abpPermissions = useAbpPermissions();

  // Extract user roles from OIDC profile
  const userRoles = useMemo(() => {
    return extractRolesFromUser(oidcAuth.user?.profile);
  }, [oidcAuth.user?.profile]);

  // User with roles
  const userWithRoles = useMemo((): UserWithRoles | null => {
    if (!oidcAuth.user?.profile) {
      return null;
    }

    return {
      sub: oidcAuth.user.profile.sub,
      name: oidcAuth.user.profile.name as string | undefined,
      email: oidcAuth.user.profile.email as string | undefined,
      roles: userRoles,
      ...oidcAuth.user.profile,
    };
  }, [oidcAuth.user?.profile, userRoles]);

  // Get highest role level (for hierarchy checks)
  const highestRoleLevel = useMemo(() => {
    if (userRoles.length === 0) return 0;
    return Math.max(...userRoles.map(role => RoleHierarchy[role] || 0));
  }, [userRoles]);

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (Array.isArray(role)) {
      return role.some(r => userRoles.includes(r));
    }
    return userRoles.includes(role);
  };

  /**
   * Check if user has ALL specified roles
   */
  const hasAllRoles = (roles: UserRole[]): boolean => {
    return roles.every(role => userRoles.includes(role));
  };

  /**
   * Check if user has a specific ABP permission (from database)
   * Permissions are fetched from /api/abp/application-configuration
   */
  const hasPermission = (permission: string | string[]): boolean => {
    if (Array.isArray(permission)) {
      return abpPermissions.hasAnyPermission(permission);
    }
    return abpPermissions.hasPermission(permission);
  };

  /**
   * Check if user has ALL specified ABP permissions
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return abpPermissions.hasAllPermissions(permissions);
  };

  /**
   * Check if user's role level is >= specified role level
   * (for hierarchy-based checks)
   */
  const hasRoleLevelOrAbove = (role: UserRole): boolean => {
    const requiredLevel = RoleHierarchy[role] || 0;
    return highestRoleLevel >= requiredLevel;
  };

  /**
   * Check if user is admin (top-level administrator)
   */
  const isAdmin = (): boolean => {
    return hasRole(UserRole.ADMIN);
  };

  /**
   * Check if user is admin or opsAdmin
   */
  const isAdminOrOps = (): boolean => {
    return hasRole([UserRole.ADMIN, UserRole.OPS_ADMIN]);
  };

  return {
    // OIDC auth properties
    ...oidcAuth,

    // Enhanced user object with roles
    user: userWithRoles,

    // Role data
    roles: userRoles,
    highestRoleLevel,

    // ABP permissions (from database, not hardcoded)
    permissions: abpPermissions.permissions,
    permissionsLoading: abpPermissions.isLoading,
    permissionsError: abpPermissions.error,
    refreshPermissions: abpPermissions.refresh,

    // Role check functions
    hasRole,
    hasAllRoles,
    hasRoleLevelOrAbove,
    isAdmin,
    isAdminOrOps,

    // ABP Permission check functions (uses database permissions)
    hasPermission,
    hasAllPermissions,
  };
}

/**
 * Hook to check if user has specific role(s)
 */
export function useHasRole(role: UserRole | UserRole[]): boolean {
  const { hasRole } = useAuth();
  return hasRole(role);
}

/**
 * Hook to check if user has specific ABP permission(s)
 */
export function useHasPermission(permission: string | string[]): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * Hook to get user's roles
 */
export function useUserRoles(): UserRole[] {
  const { roles } = useAuth();
  return roles;
}
